/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { useS3Upload } from "next-s3-upload";
import Dropzone from "react-dropzone";
import Router from "next/router";
import { Formik } from "formik";
import * as Yup from "yup";
import Icons from "../icons";
import Global from "../../utils/globalEvents";
import IconButton from "../IconButton";
import useUnload from "../../utils/handleBrowserNavigation";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const SubmitOfferForm = () => {
  const [confirmed, setConfirmed] = useState(false);
  const [offer, setOffer] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setfileTypeError] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [counter, setCounter] = useState(0);
  const [fileSize, setFileSize] = useState({});
  const [isFieldsEdited, setIsFieldsEdited] = useState(false);
  const { uploadToS3 } = useS3Upload();
  useUnload(isFieldsEdited, false);
  const uploadedFiles = [];
  let num = 0;

  const phoneRegExp = /^\+(?:[0-9] ?){6,14}[0-9]$/;
  const allowedFileTypes = ["docx", "pdf", "pptx", "png", "jpg"];

  const offerValidationSchema = Yup.object({
    companyName: Yup.string().required("Please provide a company name"),
    phone: Yup.string()
      .required("Please provide a phone number")
      .matches(/^[+]?\d+$/, "Must be a number")
      .min(9, "Minimum 9 numbers")
      .max(10, "Maximum 10 numbers"),
    raiseRange: Yup.string().required(
      "Please provide an estimate of what you want to raise"
    ),
    companyStage: Yup.string().required(
      "Please select the stage of your company"
    ),
    pitch: Yup.string()
      .required("Please enter an elevator pitch")
      .max(250, "Maximum 250 characters"),
    terms: Yup.boolean().oneOf(
      [true],
      "Please accept our terms and conditions"
    ),
  });

  const initialValues = {
    companyName: "",
    phone: "",
    raiseRange: "",
    companyStage: "",
    pitch: "",
    links: "",
    terms: false,
  };

  const handleSubmit = async (values, { setErrors, setStatus }) => {
    const res = await fetch("/api/offer/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...values, ...{ informationFiles: files } }),
    });
    if (res.status === 200) {
      const offerObject = await res.json();
      setConfirmed(true);
      setOffer(offerObject);
      window.scrollTo(0, 0);
    } else {
      const serverErrors = await res.json();
      serverErrors.errors.reduce(
        (errors, error) => ({ ...errors, [error.field]: error.message }),
        {}
      );
      setErrors({ pitch: "Server error" });
    }
  };

  const selectFiles = async (fls) => {
    num = counter;
    setFileSizeError(false);
    setfileTypeError(false);
    let total = 0;
    fls.map((f) => {
      const fileType = f.name.split(".").pop();
      if (!allowedFileTypes.includes(fileType)) {
        setfileTypeError(true);
      }
      if (f.size / 1000000 > 5) {
        setFileSizeError(true);
      }
    });
    const filteredFiles = fls.filter(
      (f) =>
        f.size / 1000000 < 5 &&
        allowedFileTypes.includes(f.name.split(".").pop())
    );
    filteredFiles.map((f) => {
      total = total + f.size;
      if ((total + totalSize) / 1000000 < 15) {
        setTotalSize(totalSize + total);
        uploadedFiles.push(f);
        setFileSize((fileSize) => ({
          ...fileSize,
          [num]: f.size,
        }));
        num++;
        setCounter(num);
      } else {
        setFileSizeError(true);
        return;
      }
    });
    Global.setLoading(true);
    const promises = uploadedFiles.map((file) => uploadToS3(file));
    const urls = (await Promise.all(promises)).map((f) => f.url);
    setFiles([...files, ...urls]);
    Global.setLoading(false);
  };
  const deleteFiles = async (f, index) => {
    setTotalSize(totalSize - fileSize[index]);
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    const result = Object.entries(fileSize)
      .filter(([k]) => +k !== index)
      .reduce((a, [, v], i) => ((a[i] = v), a), {});
    setCounter(counter - 1);
    setFileSize(result);
  };

  const form = (
    <Formik
      validationSchema={offerValidationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        touched,
        isSubmitting,
        values,
        handleChange,
        handleSubmit,
        dirty,
      }) => (
        <Form
          noValidate
          onSubmit={handleSubmit}
          className="g-form mb-5 text-left airy"
        >
          <Form.Group controlId="companyName">
            <Form.Label>Company Name </Form.Label>
            <Form.Control
              type="text"
              value={values.companyName}
              placeholder="ACME inc"
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(dirty);
              }}
              isValid={touched.companyName && !errors.companyName}
              isInvalid={touched.companyName && errors.companyName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.companyName}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="phone">
            <Form.Label>Phone </Form.Label>
            <Form.Control
              type="text"
              placeholder="0212345678"
              value={values.phone}
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(dirty);
              }}
              isValid={touched.phone && !errors.phone}
              isInvalid={touched.phone && errors.phone}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="raiseRange">
            <Form.Label>How much do you want to raise?</Form.Label>
            <Form.Control
              as="select"
              required
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(true);
              }}
              isValid={touched.raiseRange && !errors.raiseRange}
              isInvalid={touched.raiseRange && errors.raiseRange}
            >
              <option>Select a range</option>
              <option>$200,000 to $500,000</option>
              <option>$500,000 to $700,000</option>
              <option>$700,000 to $1,000,000</option>
              <option>$1,000,000 to $2,000,000</option>
              <option>more than $2,000,000</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.raiseRange}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="companyStage">
            <Form.Label>Company stage</Form.Label>
            <Form.Control
              as="select"
              required
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(true);
              }}
              isValid={touched.companyStage && !errors.companyStage}
              isInvalid={touched.companyStage && errors.companyStage}
            >
              <option>Select a stage</option>
              <option>startup</option>
              <option>scaleup</option>
              <option>mature company</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.companyStage}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="pitch">
            <Form.Label>Elevator pitch</Form.Label>
            <Form.Control
              required
              as="textarea"
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(true);
              }}
              isValid={touched.pitch && !errors.pitch}
              isInvalid={touched.pitch && errors.pitch}
            />
            <Form.Control.Feedback type="invalid">
              {errors.pitch}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="files">
            <Form.Label>
              Documentation, pitch deck, marketing material (optional)
            </Form.Label>
            <Dropzone
              onDrop={(e) => {
                selectFiles(e);
                setIsFieldsEdited(true);
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <section style={baseStyle}>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <p>
                      Drag &amp; drop some files here, or click to select files
                    </p>
                  </div>
                </section>
              )}
            </Dropzone>
            {fileSizeError && (
              <div className="error-text">
                Maximum file upload size is 5MB and Total Size should not exceed
                15MB{" "}
              </div>
            )}
            {fileTypeError && (
              <div className="error-text">
                Sorry, Unsupported file format. Supported file types include:
                docx, pdf, jpg, png and pptx. Please try again.
              </div>
            )}
            {files.map((f, index) => (
              <div className="mt-2" key={f}>
                <a target="_blank" href={f} rel="noreferrer">
                  <Icons.File size={16} /> {f.split("/").pop()}
                </a>
                &nbsp;&nbsp;
                <IconButton
                  icon="trash"
                  variant="outline-danger"
                  size="sm"
                  onClick={() => deleteFiles(f, index)}
                />
              </div>
            ))}
          </Form.Group>
          <Form.Group controlId="informationLinks">
            <Form.Label>Links to online content (optional)</Form.Label>
            <Form.Control
              required
              style={{ height: 160 }}
              as="textarea"
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(dirty);
              }}
              isValid={touched.links && !errors.links}
              isInvalid={touched.links && errors.links}
            />
            <Form.Control.Feedback type="invalid">
              {errors.links}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="terms">
            <Form.Check
              required
              value
              name="terms"
              label={
                <>
                  I Agree to Stride Equity&apos;s{" "}
                  <a href="/investor-terms-and-conditions" target="_blank">
                    Terms and Conditions
                  </a>
                </>
              }
              onChange={(e) => {
                handleChange(e);
                setIsFieldsEdited(dirty);
                setIsFieldsEdited(true)
              }}
              isInvalid={touched.terms && errors.terms}
              feedback={errors.terms}
            />
          </Form.Group>
          <Button
            variant="outline-primary"
            size="lg"
            disabled={isSubmitting}
            type="submit"
          >
            Submit your Application
          </Button>
        </Form>
      )}
    </Formik>
  );

  const thanks = (
    <div className="lead">
      <p>Thank you! Your offer has been submitted. </p>
      Our team will review your offer and contact you if needed.
    </div>
  );

  return !confirmed ? form : thanks;
};

export default SubmitOfferForm;
